import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './contexts/AuthContext';
import { AdminProvider } from './contexts/AdminContext';
import { CandidateProvider } from './contexts/CandidateContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Login from './components/auth/Login';
import SetPassword from './components/auth/SetPassword';
import ForceChangePassword from './components/auth/ForceChangePassword';
import LoadingSpinner from './components/common/LoadingSpinner';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PrivacyAckGate from './components/privacy/PrivacyAckGate';
import PrivacyAcknowledge from './components/privacy/PrivacyAcknowledge';
import PrivacyNoticePublic from './components/privacy/PrivacyNoticePublic';
import RoomCameraPage from './components/candidate/RoomCameraPage';
import ApiErrorNavigationHandler from './components/errors/ApiErrorNavigationHandler';
import {
  BadRequestPage,
  ErrorRouteShortcuts,
  ForbiddenPage,
  InternalServerErrorPage,
  NotFoundPage,
  ServiceUnavailablePage,
  UnauthorizedPage
} from './components/errors/ErrorStatusPages';
import './styles/globals.css';
import './styles/brand.css';
import './styles/staff.css';
import './styles/experience.css';
import './styles/access.css';
const LandingPage = lazy(() => import('./components/public/LandingPage'));
const PublicInformationPage = lazy(() => import('./components/public/PublicInformationPage'));
const PublicExperiencePage = lazy(() => import('./components/public/PublicExperiencePage'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const CandidatePortal = lazy(() => import('./components/candidate/CandidatePortal'));
const InvigilatorDashboard = lazy(() => import('./components/invigilator/InvigilatorDashboard'));

function App() {
  const { t } = useTranslation('common');

  // BrowserRouter lives in main.tsx so AppErrorBoundary (which renders ErrorPage)
  // always has router context if a Link is used, and so route-aware handlers work.
  return (
    <ThemeProvider>
      <AuthProvider>
        <AdminProvider>
          <CandidateProvider>
            <ApiErrorNavigationHandler />
            <div className="experience-root app-shell min-h-screen">
              <a href="#main-content" className="skip-link">{t('skipToMainContent')}</a>
              <main id="main-content" tabIndex={-1}>
                <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><LoadingSpinner size="lg" /></div>}>
                <Routes>
                  <Route path="/error" element={<ErrorRouteShortcuts />} />
                  <Route path="/error/400" element={<BadRequestPage />} />
                  <Route path="/error/401" element={<UnauthorizedPage />} />
                  <Route path="/error/403" element={<ForbiddenPage />} />
                  <Route path="/error/404" element={<NotFoundPage />} />
                  <Route path="/error/500" element={<InternalServerErrorPage />} />
                  <Route path="/error/503" element={<ServiceUnavailablePage />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/privacy-notice" element={<PrivacyNoticePublic />} />
                  <Route path="/room-camera" element={<RoomCameraPage />} />
                  <Route path="/proctor/join/:token" element={<RoomCameraPage />} />
                  <Route path="/invite/:token" element={<SetPassword />} />
                  <Route path="/force-password-change" element={<ForceChangePassword />} />
                  <Route
                    path="/privacy/acknowledge"
                    element={
                      <ProtectedRoute requiredRole={['super_admin', 'admin', 'candidate', 'invigilator']}>
                        <PrivacyAcknowledge />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/*"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <PrivacyAckGate>
                          <AdminDashboard />
                        </PrivacyAckGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/candidate/*"
                    element={
                      <ProtectedRoute requiredRole="candidate">
                        <PrivacyAckGate>
                          <CandidatePortal />
                        </PrivacyAckGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/invigilator/*"
                    element={
                      <ProtectedRoute requiredRole="invigilator">
                        <PrivacyAckGate>
                          <InvigilatorDashboard />
                        </PrivacyAckGate>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/product" element={<PublicInformationPage kind="product" />} />
                  <Route path="/experience" element={<PublicExperiencePage />} />
                  <Route path="/solutions" element={<PublicInformationPage kind="solutions" />} />
                  <Route path="/how-it-works" element={<PublicInformationPage kind="how" />} />
                  <Route path="/trust" element={<PublicInformationPage kind="trust" />} />
                  <Route path="/resources" element={<PublicInformationPage kind="resources" />} />
                  <Route path="/resources/candidate-guide" element={<PublicInformationPage kind="guide" />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
                </Suspense>
              </main>
            </div>
          </CandidateProvider>
        </AdminProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
